/* ====================================================================
 *  mapping — ЄДИНЕ місце, де описано форму позиції кошика і її переклад
 *  у/з документа Firestore (`users/{uid}/cartItems/{key}`).
 *
 *  До п.8 цей перелік полів був скопійований шість разів (стор ×4,
 *  сторінка кошика ×2) — будь-яке нове поле треба було не забути в усіх.
 *  Без Vue і без I/O: чисті функції, придатні до тестів (п.12).
 * ==================================================================== */

export interface CartItem {
  key: string;
  productId: string;
  variantId: string;
  sku: string;
  slug: string;
  title: string;
  price: number;
  qty: number;
  image: string;
  color: string;
  size: string;
  isExclusive: boolean;
  exclusiveType: string;
  badgeText: string;
}

export interface CartItemKeyParts {
  productId?: string;
  variantId?: string;
  color?: string;
  size?: string;
}

/**
 * Канонічний ключ позиції (він же id документа в Firestore).
 * З варіантом — productId|variantId; без варіанта — legacy-форма
 * productId|color|size (товари, які ще не переведені на варіанти).
 */
export function makeKey({
  productId,
  variantId = "",
  color = "",
  size = "",
}: CartItemKeyParts): string {
  if (productId && variantId) return [productId, variantId].join("|");
  return [productId, color, size].join("|");
}

/** Ключ, який позиція повинна мати за своїми ж полями. */
export function canonicalKeyOf(item: CartItemKeyParts): string {
  return makeKey({
    productId: item?.productId,
    variantId: item?.variantId || "",
    color: item?.color || "",
    size: item?.size || "",
  });
}

type AnyRecord = Record<string, unknown>;

/** Документ Firestore (або запис із localStorage) -> позиція кошика. */
export function toCartItem(key: string, data: unknown): CartItem {
  const r = (data || {}) as AnyRecord;
  return {
    key: String(key || ""),
    productId: String(r.productId || ""),
    variantId: String(r.variantId || ""),
    sku: String(r.sku || ""),
    slug: String(r.slug || ""),
    title: String(r.title || ""),
    price: Number(r.price || 0),
    qty: Number(r.qty || 1),
    image: String(r.image || ""),
    color: String(r.color || ""),
    size: String(r.size || ""),
    isExclusive: !!r.isExclusive,
    exclusiveType: String(r.exclusiveType || ""),
    badgeText: String(r.badgeText || ""),
  };
}

/**
 * Позиція кошика -> тіло документа. Без `key` (це id документа) і без
 * `updatedAt` — службову мітку додає remote-шар, щоб тип часу був один.
 */
export function toRemoteFields(item: Partial<CartItem>): AnyRecord {
  return {
    productId: item.productId,
    variantId: item.variantId || "",
    sku: item.sku || "",
    slug: item.slug || "",
    title: item.title || "",
    price: Number(item.price || 0),
    qty: Number(item.qty || 1),
    image: item.image || "",
    color: item.color || "",
    size: item.size || "",
    isExclusive: !!item.isExclusive,
    exclusiveType: item.exclusiveType || "",
    badgeText: item.badgeText || "",
  };
}

/**
 * Злиття двох кошиків по ключу: кількості СУМУЮТЬСЯ (гість доклав те,
 * що вже лежало в акаунті). Поля беруться з `base`, бо це стан із
 * Firestore; порожні поля добираються з `extra`.
 * Pure — без Vue і без I/O, придатне до тестів (п.12).
 */
export function mergeCartLists(
  base: CartItem[],
  extra: CartItem[],
): CartItem[] {
  const map = new Map<string, CartItem>();

  for (const it of base || []) {
    if (!it?.key) continue;
    map.set(it.key, { ...it });
  }

  for (const it of extra || []) {
    if (!it?.key) continue;
    const existing = map.get(it.key);
    if (!existing) {
      map.set(it.key, { ...it });
      continue;
    }
    existing.qty = Number(existing.qty || 0) + Number(it.qty || 0);
    if (!existing.variantId && it.variantId) existing.variantId = it.variantId;
    if (!existing.sku && it.sku) existing.sku = it.sku;
    if (!existing.image && it.image) existing.image = it.image;
    if (!existing.title && it.title) existing.title = it.title;
  }

  return sortByKey(Array.from(map.values()));
}

/**
 * Детерміноване сортування кошика — за ключем.
 * Саме такий порядок віддає Firestore для запиту без orderBy (за
 * __name__), тобто це той порядок, який сторінка кошика показувала
 * завжди; тепер він однаковий на всіх шляхах читання.
 */
export function sortByKey<T extends { key?: string }>(list: T[]): T[] {
  return [...list].sort((a, b) =>
    String(a?.key || "").localeCompare(String(b?.key || "")),
  );
}
